import React, { useCallback, Fragment, useEffect, useState } from "react";
import EventWrapper from "./EventWrapper";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import {
  deleteEvent,
  fetchCorporateEvents,
  fetchCountries,
  fetchIndustryEvents,
  fetchOwnEvents,
  fetchPublicEvents,
  unloadBookedEvents,
  unloadIndustryEvents,
  unloadOwnEvents,
  unloadPublicEvents,
} from "../../../../redux/actions";
import { connect } from "react-redux";
import {
  mapBookedEventToCalendarEvent,
  mapEventToCalendarEvent,
  statusKeyDiv,
} from "./mapEvents";
import { isCorporate, token } from "../../../../modules/auth";
import ViewEventModal from "../../../../Components/corporate/ViewEventModal";
import { countriesOptions } from "../../../../modules/countries-options";
import ViewBookedEvent from "../../../../Components/corporate/ViewBookedEvent";
import { CalendarToolbar } from "./CalendarToolbar";
import {
  companyObject,
  isEventSpecialist, isNonEventSpecialist,
  isRoleEdgeAdmin,
} from "../../../../modules/permissions";
import NotFound from "../../../NotFound";
import Loading from "../../../../Components/Loading";

const CalendarView = props => {
  const [showEventModal, setShowEventModal] = useState(false);
  const [showBookedEventModal, setShowBookedEventModal] = useState(false);
  const [event, setEvent] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const currentDate = new Date();
  const [dateRangeForFetch, setDateRangeForFetch] = useState([
    new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
      .toISOString()
      .split("T")[0],
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0)
      .toISOString()
      .split("T")[0],
  ]);
  const localizer = momentLocalizer(moment);

  useEffect(() => {
    const { token, company_id } = props;
    props.fetchCountries(token);
    props.fetchPublicEvents(token);
    if (company_id) {
      props.fetchIndustryEvents(token, company_id);
      props.fetchCorporateEvents(token, {
        company_id,
        dateRange: dateRangeForFetch,
      });
    }
    return () => {
      props.unloadIndustryEvents();
      props.unloadBookedEvents();
      props.unloadPublicEvents();
    };
  }, []);

  useEffect(() => {
    const { fetchCorporateEvents, token, company_id } = props;
    if (activeFilter === "companyEvents" || activeFilter === "all") {
      fetchCorporateEvents(token, { company_id, dateRange: dateRangeForFetch });
    }
  }, [dateRangeForFetch, activeFilter]);

  const eventFiltersHandler = filterName => {
    setActiveFilter(filterName);
    if (filterName === "all") {
      showAllEvents();
    } else if (filterName === "ownEvents") {
      fetchOwnEventsHandler();
    } else if (filterName === "companyEvents") {
      fetchCorporateEventsHandler();
    } else if (filterName === "industryEvents") {
      fetchIndustryEventsHandler();
    } else if (filterName === "publicEvents") {
      fetchPublicEventsHandler();
      fetchIndustryEventsHandler();
    }
  };

  const unloadEvents = () => {
    props.unloadIndustryEvents();
    props.unloadBookedEvents();
    props.unloadPublicEvents();
    props.unloadOwnEvents();
  };

  const fetchPublicEventsHandler = () => {
    unloadEvents();
    return props.fetchPublicEvents(props.token, "corporate");
  };

  const fetchIndustryEventsHandler = () => {
    unloadEvents();
    if (props.company_id) {
      return props.fetchIndustryEvents(props.token, props.company_id);
    }
  };

  const handleDeleteEvent = (event, token) => {
    return props.deleteEvent(event, token);
  };

  const showAllEvents = () => {
    unloadEvents();
    fetchPublicEventsHandler();
    fetchIndustryEventsHandler();
    fetchCorporateEventsHandler();
  };

  const fetchCorporateEventsHandler = () => {
    unloadEvents();
    if (props.company_id) {
      return props.fetchCorporateEvents(props.token, {
        company_id: props.company_id,
        dateRange: dateRangeForFetch,
      });
    }
  };

  const fetchOwnEventsHandler = () => {
    unloadEvents();
    if (!props.isRoleEdgeAdmin) {
      return props.fetchOwnEvents(props.token);
    }
  };

  const {
    publicEvents,
    industryEvents,
    bookedEvents,
    ownEvents,
    countries,
    isRoleEdgeAdmin,
    corporateAccountId,
    isCorporate,
    isFetchingBookedEvents,
    isNonEventSpecialist
  } = props;
  let eventsArray = [];
  let publicEventArray = publicEvents.map(publicEvent => {
    return mapEventToCalendarEvent(publicEvent, publicEvent.visibility_type);
  });
  let industryEventArray = industryEvents.map(industryEvent => {
    return mapEventToCalendarEvent(industryEvent, "industry");
  });
  let bookedEventArray = bookedEvents.map(bookedEvent => {
    return mapBookedEventToCalendarEvent(bookedEvent);
  });
  let ownEventsArray = ownEvents.map(ownEvent => {
    return mapBookedEventToCalendarEvent(ownEvent);
  });
  Array.prototype.push.apply(eventsArray, industryEventArray);
  Array.prototype.push.apply(eventsArray, bookedEventArray);
  Array.prototype.push.apply(eventsArray, publicEventArray);
  Array.prototype.push.apply(eventsArray, ownEventsArray);

  const handleSelectEvent = event => {
    if (event.bookedEvent) {
      setShowBookedEventModal(true);
    } else {
      setShowEventModal(true);
    }
    setEvent(event);
  };

  const handleEventModalClose = () => {
    setShowBookedEventModal(false);
    setShowEventModal(false);
  };

  const accountId = isCorporate ? corporateAccountId : props.match.params.slug;

  const handleNavigate = useCallback(
    (date, view) => {
      const givenDate = new Date(date);
      const lastMonth = new Date(
        givenDate.getFullYear(),
        givenDate.getMonth() - 1,
        1
      );
      const nextMonth = new Date(
        givenDate.getFullYear(),
        givenDate.getMonth() + 2,
        0
      );
      setDateRangeForFetch([
        lastMonth.toISOString().split("T")[0],
        nextMonth.toISOString().split("T")[0],
      ]);
    },
    [dateRangeForFetch]
  );

  if (accountId != corporateAccountId && !isRoleEdgeAdmin) {
    return <NotFound />;
  }

  const formats = {
    monthHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, "MMM yyyy"),
  };

  return (
    <Fragment>
      <div className="min-w-full">
        <h1 className="my-4">Events Calendar</h1>
        <div className={"bg-white p-5"}>
          {statusKeyDiv()}
          <div className="row">
            <div className={"col relative"}>
              {isFetchingBookedEvents ? (
                <div className={"calendar-loader"}>
                  <Loading />
                </div>
              ) : null}
              <Calendar
                style={{
                  height: 750,
                }}
                formats={formats}
                views={["month"]}
                onNavigate={handleNavigate}
                popup
                events={eventsArray}
                onClick={{}}
                onSelectEvent={event => handleSelectEvent(event)}
                step={60}
                showMultiDayTimes
                defaultDate={new Date()}
                localizer={localizer}
                components={{
                  event: EventWrapper,
                  toolbar: props => {
                    return (
                      <CalendarToolbar
                        {...props}
                        showAllEvents={showAllEvents}
                        filterOwnEvents={fetchOwnEventsHandler}
                        filterPublicEvents={fetchPublicEventsHandler}
                        filterIndustryEvents={fetchIndustryEventsHandler}
                        filterCorporateEvents={fetchCorporateEventsHandler}
                        isEdgeAdmin={isRoleEdgeAdmin}
                        eventFiltersHandler={eventFiltersHandler}
                        activeFilter={activeFilter}
                        isNonEventSpecialist={isNonEventSpecialist}
                      />
                    );
                  },
                }}
              />
              {showEventModal && (
                <ViewEventModal
                  event={event}
                  onClose={handleEventModalClose}
                  countries={countriesOptions(countries)}
                  deleteEvent={handleDeleteEvent}
                />
              )}
              {showBookedEventModal && (
                <ViewBookedEvent
                  event={event}
                  onClose={handleEventModalClose}
                  countries={countriesOptions(countries)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state, props) => {
  let company;
  let isCorporateAccount = isCorporate(state);
  if (isCorporateAccount) {
    company = companyObject(state.auth);
  }
  return {
    ...state.publicEvents,
    ...state.industryEvents,
    ...state.bookedEvents,
    ...state.countries,
    token: token(state),
    company_id: company
      ? company.id
      : props.account && props.account.company
      ? props.account.company.id
      : 0,
    corporateAccountId: props.account && props.account.id,
    isRoleEdgeAdmin: isRoleEdgeAdmin(state.auth),
    isEventSpecialist: isEventSpecialist(state.auth),
    isCorporate: isCorporateAccount,
    isNonEventSpecialist: isNonEventSpecialist(state.auth),
  };
};

const mapDispatchToProps = {
  fetchIndustryEvents,
  fetchPublicEvents,
  fetchCorporateEvents,
  fetchOwnEvents,
  fetchCountries,
  unloadIndustryEvents,
  unloadPublicEvents,
  unloadOwnEvents,
  unloadBookedEvents,
  deleteEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarView);
